<template>
  <div id="campaignRetargetingModal" class="modal fade" tabindex="-1">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Campaign Retargeting</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label class="form-label">Campaign Selection Method</label>
            <div class="form-text mb-2">Choose how to select campaigns for retargeting</div>
            
            <!-- Method Selection -->
            <div class="mb-3">
              <div class="form-check">
                <input class="form-check-input" type="radio" v-model="selectionMethod" value="manual" id="methodManual">
                <label class="form-check-label" for="methodManual">
                  Enter Campaign ID(s) manually
                </label>
              </div>
              <div class="form-check">
                <input class="form-check-input" type="radio" v-model="selectionMethod" value="customer" id="methodCustomer">
                <label class="form-check-label" for="methodCustomer">
                  Select from customer campaigns
                </label>
              </div>
            </div>

            <!-- Manual Campaign ID Input -->
            <div v-if="selectionMethod === 'manual'" class="mb-3">
              <label class="form-label">Campaign ID(s)</label>
              <div class="form-text mb-2">Enter one or more campaign IDs separated by commas</div>
              <input 
                type="text" 
                class="form-control" 
                v-model="campaignIdInput"
                placeholder="e.g., 1234, 5678, 9012"
                @input="onCampaignIdInput"
              />
            </div>

            <!-- Customer-based Campaign Selection -->
            <div v-if="selectionMethod === 'customer'" class="mb-3">
              <div class="row">
                <div class="col-md-6">
                  <label class="form-label">Customer</label>
                  <select class="form-select" v-model="selectedCustomer" @change="onCustomerChange">
                    <option value="">Select a customer...</option>
                    <option v-for="customer in customers" :key="customer.customerId" :value="customer">
                      {{ customer.customerName }}
                    </option>
                  </select>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Campaigns</label>
                  <select-dropdown
                    :list="customerCampaigns"
                    labelField="campaignRef"
                    listTitle="Campaigns"
                    :singleSelect="false"
                    :disabled="!selectedCustomer"
                    @update:selectedItems="onCampaignsSelected"
                  />
                  <div class="form-text">Select one or more campaigns</div>
                </div>
              </div>
            </div>

            <div class="text-danger small mt-1" v-if="campaignError">
              {{ campaignError }}
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label">Data Partner</label>
            <div class="form-text mb-2">Select the data partner for retargeting</div>
            <select class="form-select" v-model="selectedDataPartner" @change="onDataPartnerChange">
              <option value="">Select a data partner...</option>
              <option v-for="partner in dataPartners" :key="partner.name" :value="partner">
                {{ partner.name }}
              </option>
            </select>
            <div class="text-danger small mt-1" v-if="dataPartnerError">
              {{ dataPartnerError }}
            </div>
          </div>

          <div v-if="showViewabilityBin" class="mb-3">
            <label class="form-label">Viewability Bin</label>
            <div class="form-text mb-2">Select the viewability metric for the {{ selectedDataPartner.name }} export</div>
            <select class="form-select" v-model="selectedViewabilityBin" @change="onAbcsViewabilityChange">
              <option
                v-for="option in abcsViewabilityOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
            <div class="text-danger small mt-1" v-if="viewabilityBinError">
              {{ viewabilityBinError }}
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label">Frequency</label>
            <div class="form-text mb-2">Toggle between daily and weekly retargeting frequency</div>
            <div class="form-check form-switch">
              <input
                class="form-check-input"
                type="checkbox"
                role="switch"
                id="isOneWeekFrequency"
                v-model="isOneWeekFrequency"
              />
              <label class="form-check-label" for="isOneWeekFrequency">
                {{ isOneWeekFrequency ? 'Weekly frequency' : 'Daily frequency' }}
              </label>
            </div>
          </div>

          <div v-if="showAccretiveCsvColumns" class="mb-3">
            <label class="form-label">Export columns</label>
            <div class="form-text mb-2">
              Choose the fields included in each row of the Accretive export file. Selecting any field beyond Device Hash
              produces one row per exposure event (a device can appear multiple times).
            </div>
            <div class="retargeting-csv-columns-list border rounded p-3">
              <div
                class="form-check"
                v-for="column in accretiveCsvColumnOptions"
                :key="column.value"
              >
                <input
                  class="form-check-input"
                  type="checkbox"
                  :id="`retargetingCsvColumn-${column.value}`"
                  :value="column.value"
                  :checked="isAccretiveCsvColumnSelected(column.value)"
                  :disabled="column.locked"
                  @change="onAccretiveCsvColumnChange(column, $event.target.checked)"
                />
                <label class="form-check-label" :for="`retargetingCsvColumn-${column.value}`">
                  {{ column.label }}
                  <span v-if="column.locked" class="text-muted">(required)</span>
                </label>
                <div v-if="column.note" class="form-text mt-0">
                  {{ column.note }}
                </div>
              </div>
            </div>
            <div v-if="hasEventLevelAccretiveColumns" class="form-text text-warning mt-2">
              Additional fields create a larger event-level export where device hashes can repeat.
            </div>
          </div>

          <div v-if="showAbcsCsvColumns" class="mb-3">
            <label class="form-label">Export columns</label>
            <div class="form-text mb-2">
              Choose the fields included in each row of the ABCS export file. At least one column must be selected.
            </div>
            <div class="retargeting-csv-columns-list border rounded p-3">
              <div
                class="form-check"
                v-for="column in abcsCsvColumnOptions"
                :key="column.value"
              >
                <input
                  class="form-check-input"
                  type="checkbox"
                  :id="`abcsCsvColumn-${column.value}`"
                  :value="column.value"
                  :checked="isAbcsCsvColumnSelected(column.value)"
                  @change="onAbcsCsvColumnChange(column, $event.target.checked)"
                />
                <label class="form-check-label" :for="`abcsCsvColumn-${column.value}`">
                  {{ column.label }}
                </label>
                <div v-if="column.note" class="form-text mt-0">
                  {{ column.note }}
                </div>
              </div>
            </div>
            <div class="text-danger small mt-1" v-if="abcsCsvColumnsError">
              {{ abcsCsvColumnsError }}
            </div>
          </div>

        </div>

        <div class="modal-footer d-block">
          <div class="d-flex justify-content-end">
            <button type="button" class="btn btn-secondary text-white" @click="onSubmitClick" :disabled="!canSubmit || isSubmitting">
              <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2" role="status"></span>
              {{ submitButtonLabel }}
            </button>
            <button type="button" class="btn btn-primary ms-2" data-bs-dismiss="modal">Cancel</button>
          </div>
          <div v-if="showOverrideWarning" class="alert alert-warning text-start mt-3 mb-0">
            <div class="fw-bold mb-1">Existing retargeting study found</div>
            <div>
              {{ overrideWarningMessage }}
            </div>
            <div class="form-check form-switch mt-3">
              <input
                class="form-check-input"
                type="checkbox"
                role="switch"
                id="overrideExistingStudy"
                v-model="overrideExistingStudy"
                :disabled="isSubmitting"
              />
              <label class="form-check-label" for="overrideExistingStudy">
                Override and delete existing study
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CampaignService from "@/services/CampaignService";
import AppUtil from "@/utils/AppUtil";
import SelectDropdown from "@/components/SelectDropdown";
import streetmetrics from "@/model";
import http from 'superagent';
import {
  DEVICE_HASH_COLUMN,
  RETARGETING_CSV_COLUMN_OPTIONS,
  getRetargetingCsvColumnsInOrder,
} from "@/data/retargetingCsvColumns";
import {
  ABCS_CSV_COLUMN_OPTIONS,
  ABCS_DEFAULT_VIEWABILITY_BIN,
  ABCS_VIEWABILITY_OPTIONS,
  getAbcsCsvColumnsInOrder,
  getDefaultAbcsCsvColumns,
} from "@/data/retargetingAbcs";
import {
  isAbcsDataPartner,
  isAccretiveDataPartner,
  RETARGETING_DATA_PARTNERS,
} from "@/data/retargetingDataPartners";

export default {
  name: 'CampaignRetargetingModal',
  components: { SelectDropdown },
  props: {
    yesEvent: Function,
  },
  data() {
    return {
      model: streetmetrics.model,
      currentModal: null,
      modalElement: null,
      campaignService: new CampaignService(),
      campaignIdInput: '',
      campaignError: null,
      isSubmitting: false,
      selectionMethod: 'manual',
      selectedCustomer: null,
      selectedCampaigns: [],
      customers: [],
      customerCampaigns: [],
      dataPartners: RETARGETING_DATA_PARTNERS,
      selectedDataPartner: null,
      dataPartnerError: null,
      isOneWeekFrequency: false,
      selectedViewabilityBin: ABCS_DEFAULT_VIEWABILITY_BIN,
      abcsViewabilityOptions: ABCS_VIEWABILITY_OPTIONS,
      abcsCsvColumnOptions: ABCS_CSV_COLUMN_OPTIONS,
      selectedAbcsCsvColumns: getDefaultAbcsCsvColumns(),
      accretiveCsvColumnOptions: RETARGETING_CSV_COLUMN_OPTIONS,
      selectedAccretiveCsvColumns: [DEVICE_HASH_COLUMN],
      viewabilityBinError: null,
      abcsCsvColumnsError: null,
      overrideExistingStudy: false,
      showOverrideWarning: false,
      overrideWarningMessage: ''
    }
  },
  computed: {
    canSubmit() {
      if (this.selectionMethod === 'manual') {
        return this.campaignIdInput && this.campaignIdInput.trim() !== ''
      } else if (this.selectionMethod === 'customer') {
        return this.selectedCustomer && this.selectedCampaigns.length > 0
      }
      return false
    },
    showViewabilityBin() {
      return isAbcsDataPartner(this.selectedDataPartner) || isAccretiveDataPartner(this.selectedDataPartner)
    },
    showAbcsCsvColumns() {
      return isAbcsDataPartner(this.selectedDataPartner)
    },
    showAccretiveCsvColumns() {
      return isAccretiveDataPartner(this.selectedDataPartner)
    },
    selectedAbcsCsvExportColumns() {
      return getAbcsCsvColumnsInOrder(this.selectedAbcsCsvColumns)
    },
    selectedAccretiveCsvExportColumns() {
      return getRetargetingCsvColumnsInOrder(
        this.selectedAccretiveCsvColumns,
        this.showAccretiveCsvColumns
      )
    },
    hasEventLevelAccretiveColumns() {
      return this.selectedAccretiveCsvExportColumns.some(column => column !== DEVICE_HASH_COLUMN)
    },
    submitButtonLabel() {
      if (this.isSubmitting) {
        return 'Submitting...'
      }

      return this.overrideExistingStudy ? 'Replace Study and Start Export' : 'Submit for Retargeting'
    }
  },
  mounted() {
    this.modalElement = document.getElementById('campaignRetargetingModal')
    this.currentModal = new bootstrap.Modal(this.modalElement)

    this.modalElement.addEventListener('hidden.bs.modal', () => {
      this.$emit('noEvent')
    })

    this.loadData()
    this.currentModal.show()
  },
  beforeDestroy() {
    this.modalElement.removeEventListener('hidden.bs.modal')
  },
  methods: {
    async loadData() {
      try {
        // Load customers
        this.customers = this.model.customers || []
      } catch (error) {
        console.error('Error loading customers:', error)
      }
    },

    onCampaignIdInput() {
      this.campaignError = null
      this.clearOverrideConfirmation()
    },

    async onCustomerChange() {
      this.selectedCampaigns = []
      this.customerCampaigns = []
      this.campaignError = null
      
      if (this.selectedCustomer) {
        try {
          const response = await this.campaignService.getCustomerCampaigns(this.selectedCustomer.customerId)
          this.customerCampaigns = response.data || []
        } catch (error) {
          console.error('Error loading customer campaigns:', error)
          this.model.toast.background = 'bg-red'
          this.model.toast.message = 'Error loading customer campaigns. Please try again.'
        }
      }
    },

    onCampaignsSelected(selectedItems) {
      this.selectedCampaigns = selectedItems
      this.campaignError = null
      this.clearOverrideConfirmation()
    },

    onDataPartnerChange() {
      this.dataPartnerError = null
      this.viewabilityBinError = null
      this.abcsCsvColumnsError = null
      this.clearOverrideConfirmation()
      this.resetPartnerSpecificFields()
    },

    resetPartnerSpecificFields() {
      if (isAbcsDataPartner(this.selectedDataPartner)) {
        this.selectedViewabilityBin = ABCS_DEFAULT_VIEWABILITY_BIN
        this.selectedAbcsCsvColumns = getDefaultAbcsCsvColumns()
        return
      }

      if (isAccretiveDataPartner(this.selectedDataPartner)) {
        this.selectedViewabilityBin = ABCS_DEFAULT_VIEWABILITY_BIN
        this.selectedAccretiveCsvColumns = [DEVICE_HASH_COLUMN]
      }
    },

    onAbcsViewabilityChange() {
      this.viewabilityBinError = null
      this.clearOverrideConfirmation()
    },

    isAbcsCsvColumnSelected(columnValue) {
      return this.selectedAbcsCsvExportColumns.includes(columnValue)
    },

    onAbcsCsvColumnChange(column, checked) {
      if (checked) {
        if (!this.selectedAbcsCsvColumns.includes(column.value)) {
          this.selectedAbcsCsvColumns.push(column.value)
        }
      } else {
        this.selectedAbcsCsvColumns = this.selectedAbcsCsvColumns.filter(
          selectedColumn => selectedColumn !== column.value
        )
      }

      this.abcsCsvColumnsError = null
      this.clearOverrideConfirmation()
    },

    isAccretiveCsvColumnSelected(columnValue) {
      return this.selectedAccretiveCsvExportColumns.includes(columnValue)
    },

    onAccretiveCsvColumnChange(column, checked) {
      if (column.locked) {
        this.ensureDefaultAccretiveCsvColumns()
        return
      }

      if (checked) {
        if (!this.selectedAccretiveCsvColumns.includes(column.value)) {
          this.selectedAccretiveCsvColumns.push(column.value)
        }
      } else {
        this.selectedAccretiveCsvColumns = this.selectedAccretiveCsvColumns.filter(
          selectedColumn => selectedColumn !== column.value
        )
      }

      this.ensureDefaultAccretiveCsvColumns()
    },

    ensureDefaultAccretiveCsvColumns() {
      if (!this.selectedAccretiveCsvColumns.includes(DEVICE_HASH_COLUMN)) {
        this.selectedAccretiveCsvColumns.unshift(DEVICE_HASH_COLUMN)
      }
    },

    clearOverrideConfirmation() {
      this.overrideExistingStudy = false
      this.showOverrideWarning = false
      this.overrideWarningMessage = ''
    },

    validateForm() {
      if (this.selectionMethod === 'manual') {
        if (!this.campaignIdInput || this.campaignIdInput.trim() === '') {
          this.campaignError = 'Please enter at least one campaign ID'
          return false
        }
      } else if (this.selectionMethod === 'customer') {
        if (!this.selectedCustomer) {
          this.campaignError = 'Please select a customer'
          return false
        }
        if (this.selectedCampaigns.length === 0) {
          this.campaignError = 'Please select at least one campaign'
          return false
        }
      }
      
      if (!this.selectedDataPartner) {
        this.dataPartnerError = 'Please select a data partner'
        return false
      }

      if (this.showViewabilityBin && !this.selectedViewabilityBin) {
        this.viewabilityBinError = 'Please select a viewability bin'
        return false
      }

      if (isAbcsDataPartner(this.selectedDataPartner)) {
        if (this.selectedAbcsCsvExportColumns.length === 0) {
          this.abcsCsvColumnsError = 'Please select at least one export column'
          return false
        }
      }
      
      this.campaignError = null
      this.dataPartnerError = null
      this.viewabilityBinError = null
      this.abcsCsvColumnsError = null
      return true
    },

    async onSubmitClick() {
      if (!this.validateForm()) {
        return
      }

      this.isSubmitting = true
      
      try {
        const request = this.buildCreateRetargetingStudyRequest()
        const result = await this.submitCreateRetargetingStudyRequest(request)
        this.handleCreateRetargetingStudySuccess(result, request.campaignIds)
      } catch (error) {
        console.error('Error submitting campaigns for retargeting:', error)
        this.handleCreateRetargetingStudyError(error)
      } finally {
        this.isSubmitting = false
      }
    },

    buildCreateRetargetingStudyRequest() {
      const campaignIds = this.getSelectedCampaignIds()
      const request = {
        campaignIds: campaignIds,
        dataPartner: this.selectedDataPartner.name,
        isOneWeekFrequency: this.isOneWeekFrequency
      }

      if (isAbcsDataPartner(this.selectedDataPartner)) {
        request.retargetingViewabilityBin = this.selectedViewabilityBin
        request.retargetingCsvColumns = this.selectedAbcsCsvExportColumns
      } else if (this.showAccretiveCsvColumns) {
        request.retargetingViewabilityBin = this.selectedViewabilityBin
        // Always send the list, even when it is only DEVICE_HASH: an omitted list
        // makes Helios fall back to its full column default (every ABCS + Accretive token).
        request.retargetingCsvColumns = this.selectedAccretiveCsvExportColumns
      }

      if (this.showOverrideWarning || this.overrideExistingStudy) {
        request.override = this.overrideExistingStudy
      }

      return request
    },

    getSelectedCampaignIds() {
      if (this.selectionMethod === 'manual') {
        // Parse comma-separated campaign IDs
        return this.campaignIdInput
          .split(',')
          .map(id => id.trim())
          .filter(id => id !== '')
          .map(id => parseInt(id))
          .filter(id => !isNaN(id))
      }

      if (this.selectionMethod === 'customer') {
        // Use selected campaigns from customer dropdown
        return this.selectedCampaigns.map(campaign => campaign.campaignId)
      }

      return []
    },

    async submitCreateRetargetingStudyRequest(request) {
      const url = new AppUtil().getBaseUrl() + '/v3/api/attribution/create-retargeting-study'

      return await new Promise((resolve, reject) => {
        http.post(url)
          .send(request)
          .set('Authorization', 'Bearer ' + this.model.authToken)
          .set('Accept', 'application/json')
          .end((error, response) => {
            if (response && (response.status === 200 || response.status === 201)) {
              resolve(JSON.parse(response.text))
            } else {
              reject(this.parseRetargetingStudyError(error, response))
            }
          })
      })
    },

    parseRetargetingStudyError(error, response) {
      if (response && response.body && Object.keys(response.body).length > 0) {
        return {
          ...response.body,
          status: response.status
        }
      }

      if (response && response.text) {
        try {
          return {
            ...JSON.parse(response.text),
            status: response.status
          }
        } catch (parseError) {
          return {
            message: response.text,
            status: response.status
          }
        }
      }

      if (error && error.response && error.response.body && Object.keys(error.response.body).length > 0) {
        return {
          ...error.response.body,
          status: error.status || error.response.status
        }
      }

      if (error && error.response && error.response.text) {
        try {
          return {
            ...JSON.parse(error.response.text),
            status: error.status || error.response.status
          }
        } catch (parseError) {
          return {
            message: error.response.text,
            status: error.status || error.response.status
          }
        }
      }

      if (error) {
        return {
          ...error,
          status: error.status,
          message: error.message
        }
      }

      return {
        message: 'Error submitting campaigns for retargeting. Please try again.'
      }
    },

    handleCreateRetargetingStudyError(error) {
      if (this.isDuplicateRetargetingStudyError(error)) {
        this.overrideExistingStudy = false
        this.showOverrideWarning = true
        this.overrideWarningMessage = this.getRetargetingStudyErrorMessage(error) || 'A retargeting study already exists for the selected campaign and data partner. Enable override to delete the existing study and create a new export.'
        return
      }

      this.model.toast.background = 'bg-red'
      this.model.toast.message = this.getRetargetingStudyErrorMessage(error)
    },

    isDuplicateRetargetingStudyError(error) {
      const message = this.getRetargetingStudyErrorText(error)
      const statusCode = error && (error.status || error.statusCode)
      return error && (
        statusCode === 409 ||
        (statusCode === 400 && message.includes('override')) ||
        message.includes('already exist') ||
        message.includes('already exists') ||
        message.includes('duplicate') ||
        message.includes('pass override') ||
        (message.includes('retargeting') && message.includes('exist'))
      )
    },

    getRetargetingStudyErrorText(error) {
      if (!error) {
        return ''
      }

      return String(
        error.message ||
        error.error ||
        error.detail ||
        error.title ||
        error.description ||
        error.reason ||
        this.getNestedRetargetingStudyErrorMessage(error) ||
        JSON.stringify(error) ||
        ''
      ).toLowerCase()
    },

    getRetargetingStudyErrorMessage(error) {
      const message = error && (
        error.message ||
        error.error ||
        error.detail ||
        error.description ||
        error.reason ||
        this.getNestedRetargetingStudyErrorMessage(error)
      )

      return message || 'Error submitting campaigns for retargeting. Please try again.'
    },

    getNestedRetargetingStudyErrorMessage(error) {
      const nestedErrors = error && (error.errors || error.issues || error.causes)

      if (Array.isArray(nestedErrors)) {
        const nestedMessage = nestedErrors
          .map(nestedError => {
            if (typeof nestedError === 'string') {
              return nestedError
            }

            return nestedError.message || nestedError.error || nestedError.detail || nestedError.description
          })
          .filter(Boolean)
          .join(' ')

        if (nestedMessage) {
          return nestedMessage
        }
      }

      return ''
    },

    handleCreateRetargetingStudySuccess(result, campaignIds) {
      const campaignCount = campaignIds.length
      const successMessage = result.message || `Successfully created ${campaignCount} retargeting stud${campaignCount !== 1 ? 'ies' : 'y'}`
      const exportStartedMessage = `${successMessage}. Export started.`

      if (result.studies && result.studies.length > 0) {
        const attributionIds = result.studies.map(study => study.attributionId).join(', ')
        this.model.toast.background = 'bg-green'
        this.model.toast.message = `${exportStartedMessage} Attribution IDs: ${attributionIds}`
      } else {
        this.model.toast.background = 'bg-green'
        this.model.toast.message = exportStartedMessage
      }

      // Call the parent's yesEvent if provided, passing the full result including studies
      if (this.yesEvent) {
        this.yesEvent(result)
      }

      // Close the modal
      this.currentModal.hide()
    },
  }
}
</script>

<style scoped>
.w-100-pct {
  width: 100%;
}
</style> 